import { ArrowDropDown, ArrowForward } from '@mui/icons-material';
import { Alert, Autocomplete, Box, Button, ButtonGroup, Checkbox, CircularProgress, ClickAwayListener, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Grow, IconButton, MenuItem, MenuList, Paper, Popper, TextField, Tooltip, Typography } from '@mui/material';
import { assign, cloneDeep } from 'lodash';
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import Api from '../../Api';
import { BasketContext } from '../../contexts/BasketContext';
import { Feature, PermissionContext } from '../../contexts/PermissionContext';
import { ReturnContext } from '../../contexts/ReturnContext';
import { IAssignedMaterial, IBasketItem, IComboBoxOption, IContract, IContractVariation, IDispatchRequest, IDispatchMaterial, IBasketDispatch, IItemValidationModel, IStorageRequest } from '../../interfaces';
import { MaterialDispatchFormControl } from '../Shared/FormControls/MaterialDispatchFormControl';

export const BasketReturnDialog: React.FC = () => {

    const [open, setOpen] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [title, setTitle] = useState("Storage Request");
    const [dispatched, setDispatched] = useState(false);
    const [targetOptions, setTargetOptions] = useState<IComboBoxOption[]>([]);
    const [selectedOption, setSelectedOption] = useState<IComboBoxOption | undefined>();
    const [newEntity, setNewEntity] = useState<boolean | undefined>(undefined);

    const [CVContractIds, setCVContractIds] = useState<{ cvId: string, cId: string }[]>([]);

    const [validBasketItems, setValidBasketItems] = useState<IItemValidationModel[]>([]);
    const [assigned, setAssigned] = useState<IItemValidationModel[]>([]);
    const [loadingMaterials, setLoadingMaterials] = useState(true);

    const basketContext = useContext(ReturnContext);
    const permissionContext = useContext(PermissionContext);
    const anchorRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const onStorageRequest = () => {

        setNewEntity(false);

        Api.StorageRequests.getByStatus("Draft").then((drafts) => {
            Api.StorageRequests.getByStatus("RejectedOpen").then((rejected) => {

                let all = (drafts as any[]).concat(rejected);

                let options = (all as IStorageRequest[]).map(storageRequest => {
                    return {
                        id: storageRequest.storageRequestId,
                        value: storageRequest.reference ?? `No Reference (created ${new Date(storageRequest.dateCreated ?? "").toLocaleDateString("en-GB")})`
                    } as IComboBoxOption
                });

                setTargetOptions(options);
            })
        });

        setOpenDialog(true);
    }

    const handleAdjustQuantity = (basketItem: IBasketItem, materialId: string | undefined, subtract: boolean = false) => {
        let assignedMaterials = cloneDeep(assigned);
        let updated = false;

        assignedMaterials.forEach(item => {

            if (item.itemId === basketItem.id) {
                item.materials.forEach(material => {

                    if (material.id === materialId) {
                        if (subtract) material.quantity = material.quantity === 0 ? 0 : (material.quantity - 1);
                        else {

                            let max = basketItem.materialGroup.materials?.filter(x => x.materialId === materialId)[0].quantity;

                            if (material.quantity !== max)
                                material.quantity++;
                        };

                        updated = true;
                    }
                });


                if (!updated)
                    item.materials.push({ id: materialId ?? "", quantity: subtract ? 0 : 1 })


            }
        });

        setAssigned(assignedMaterials);

        handleValidation(assignedMaterials);
    }
    const handleSetQuantity = (basketItem: IBasketItem, materialId: string | undefined, qty: number) => {

        let assignedMaterials = cloneDeep(assigned);
        let updated = false;

        assignedMaterials.forEach(item => {

            if (item.itemId === basketItem.id) {

                let max = basketItem.materialGroup.materials?.filter(x => x.materialId === materialId)[0].quantity ?? 0;

                if (qty > max) qty = max;


                item.materials.forEach(material => {
                    if (material.id === materialId) {
                        material.quantity = qty;
                        updated = true;
                    }
                })


                if (!updated)
                    item.materials.push({ id: materialId ?? "", quantity: qty })
            }

        });

        setAssigned(assignedMaterials);

        handleValidation(assignedMaterials);

    }
    const handleCloseDispatch = (event: Event) => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
        ) { return; }

        setOpen(false);
    };
    const handleValidation = (items: IItemValidationModel[]) => {

        let newValidBasketItems: IItemValidationModel[] = [];

        items.forEach(item => {
            newValidBasketItems.push({
                itemId: item.itemId,
                materials: item.materials,
                fulfilled: false
            });
        });

        setValidBasketItems(newValidBasketItems);

    }

    const getValidState = () => {

        if (selectedOption?.id === undefined) {
            if (newEntity !== true)
                return false;
        }

        let valid = true;

        basketContext.basket.items.every(item => {


            let basketItem = validBasketItems.filter(x => x.itemId === item.id)[0];

            if (basketItem) {

                if (item.requestedQuantity !== basketItem.materials.reduce((sum, a) => sum + a.quantity, 0))
                    valid = false;
            } else valid = false;

            return valid;

        });

        return valid;
    }

    const autoAssign = (basketItem: IBasketItem) => {

        let autoAssigned: IAssignedMaterial[] = [];

        if (basketItem.materialGroup.materials?.length === 1) {

            //there's only one material so quantity
            //must come from it

            let single = [{
                id: basketItem.materialGroup.materials[0].materialId ?? "",
                quantity: basketItem.requestedQuantity
            }] as IAssignedMaterial[];

            autoAssigned = single;

        } else {


            let materials = cloneDeep(basketItem.materialGroup.materials)?.sort(
                (a, b) => (b.quantity ?? 0) - (a.quantity ?? 0)
            );

            if (materials) {

                let assignedMaterials: IAssignedMaterial[] = [];

                materials.every(material => {

                    let materialId = material.materialId ?? "";
                    let available = material.quantity ?? 0;

                    if (available >= basketItem.requestedQuantity) {

                        //if one of the materials has enough qty
                        //take from the material with most available
                        assignedMaterials = [{
                            id: materialId,
                            quantity: basketItem.requestedQuantity
                        }];
                        return false;

                    } else {
                        let currentQty = assignedMaterials.reduce((acc, obj) => { return acc + obj.quantity }, 0)
                        let unassignedQty = basketItem.requestedQuantity - currentQty;

                        if (unassignedQty === 0) return false; //no need to look for more

                        assignedMaterials.push({
                            id: materialId,
                            quantity: available >= unassignedQty ? unassignedQty : available
                        })
                    }

                    return true; //continue iterating

                });
                autoAssigned = assignedMaterials;
            }
        }


        return {
            itemId: basketItem.id,
            materials: autoAssigned
        } as IItemValidationModel;

    }
    const isValid = (basketItem: IBasketItem, materials: IAssignedMaterial[]) => {
        let validState = false;


        let assignedQty = materials.reduce((acc, obj) => { return acc + obj.quantity }, 0);
        if (assignedQty === basketItem.requestedQuantity) validState = true;

        return validState;
    }
    const confirmDispatch = async () => {

        setLoadingMaterials(true);


        let materials: IDispatchMaterial[] = [];

        validBasketItems.forEach(item => {
            item.materials.forEach(material => {
                materials.push(material as IDispatchMaterial)
            })
        })

        let basketDispatch: IBasketDispatch = {
            id: selectedOption?.id,
            materials: materials
        };

        if (newEntity)
            await Api.StorageRequests.create().then((response: any) => {

                let sr = response as IStorageRequest

                if (sr.storageRequestId) {
                    basketDispatch.id = sr.storageRequestId;
                }

                let newOption = {
                    id: sr.storageRequestId ?? "",
                    value: "New Storage Request"
                };


                setNewEntity(undefined);
                setTargetOptions([...targetOptions, newOption])
                setSelectedOption(newOption)
            });

        //basketDispatch.materials = undefined; //no exact materials are required
        basketDispatch.materialGroups = basketContext.basket.items;

        Api.Basket.dispatchToStorageRequest(basketDispatch).then((response) => {

            basketContext.clearBasket();
            setLoadingMaterials(false);
            setDispatched(true);
        });

    }
    const browseToTarget = () => {
        if (selectedOption) {
            navigate('/storagerequest/update/' + selectedOption.id)
        }
    }



    useEffect(() => {

        let autoAssigned: IItemValidationModel[] = [];

        basketContext.basket.items.forEach(item => {
            autoAssigned.push(autoAssign(item));
        });

        setAssigned(autoAssigned);
        handleValidation(autoAssigned);
        setLoadingMaterials(false);

    }, [basketContext.basket, open])

    useEffect(() => {
    }, [assigned])

    return (
        <span>
            <ButtonGroup ref={anchorRef}>
                <Tooltip title="return materials">
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={onStorageRequest}
                    >
                        Return
                    </Button>
                </Tooltip>
            </ButtonGroup>


            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                fullWidth
                maxWidth={dispatched ? "xs" : "lg"}
            >
                <DialogTitle>
                    Return via {title}
                </DialogTitle>
                <DialogContent>

                    <>



                        {dispatched ?
                            <Typography color="lightgray" variant="subtitle2">Materials have been dispatched ✓</Typography>
                            :
                            <Typography>Select the {title} you would like to assign material to.</Typography>
                        }</>

                    <Grid container marginTop={2}>
                        <Grid xs={12} item display="flex" justifyContent="space-between" alignItems="center">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>

                                {dispatched ? <Typography>{selectedOption?.value}</Typography> :

                                    <Autocomplete
                                        sx={{ width: '20rem' }}
                                        id="dispatch-target-select"
                                        options={targetOptions.map(x => x.value)}
                                        value={selectedOption?.value}
                                        onChange={(_, value) => setSelectedOption({ value, id: targetOptions.filter(x => x.value === value)[0]?.id })}
                                        renderInput={(params) => <TextField
                                            {...params}
                                            label={title}
                                            required
                                        />}
                                        disabled={dispatched || newEntity === true}
                                    />
                                }

                                {
                                    newEntity !== undefined && (!dispatched) &&
                                    <>
                                        <Typography sx={{ mx: 4, my: 0 }} color="lightgray" variant="overline">or</Typography>

                                        <FormControlLabel
                                            control={<Checkbox
                                                checked={newEntity}
                                                onChange={(_, checked) => { setNewEntity(checked); }}
                                            />}
                                            label={"New " + title}
                                        />
                                    </>
                                }
                            </Box>

                            {dispatched &&
                                <Tooltip title={`View ${title}`}>
                                    <IconButton
                                        sx={{ ml: 1 }}
                                        color="primary"
                                        onClick={() => browseToTarget()}
                                    >
                                        <ArrowForward />
                                    </IconButton>
                                </Tooltip>
                            }

                        </Grid><Grid xs={12} item>
                            {loadingMaterials ? <CircularProgress /> :
                                !dispatched && basketContext.basket.items.map(basketItem => {

                                    let assignedMaterials = assigned.filter(x => x.itemId === basketItem.id)[0].materials

                                    return (<MaterialDispatchFormControl
                                        key={basketItem.id}
                                        requestedItem={basketItem}
                                        assigned={assignedMaterials}
                                        valid={isValid(basketItem, assignedMaterials)}
                                        onAdjustQuantity={(m, s) => handleAdjustQuantity(basketItem, m, s)}
                                        onSetQuantity={(m, q) => handleSetQuantity(basketItem, m, q)}
                                        readonly={false}
                                    />);
                                })
                            }
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{}}>
                    {!dispatched && <>
                        <Button onClick={() => setOpenDialog(false)} color="warning">Cancel</Button>
                        <Button
                            onClick={confirmDispatch}
                            color="primary"
                            disabled={!getValidState()}

                        >Return</Button>
                    </>}
                </DialogActions>
            </Dialog>
        </span>
    );
}
